import React, { useState } from "react";
import CloseIcon from "@mui/icons-material/Close";
import {
  Box,
  IconButton,
  Typography,
  Radio,
  RadioGroup,
  FormControlLabel,
  TextField,
  Button,
  Divider,
  Alert,
  Chip,
  Modal,
  Card,
  CardMedia,
  CardContent,
  Drawer,
  LinearProgress,
} from "@mui/material";
import CloudUploadOutlinedIcon from "@mui/icons-material/CloudUploadOutlined";
import DeleteIcon from "@mui/icons-material/Delete";
import ClearIcon from "@mui/icons-material/Clear";
import {
  getFileSignedURLThunk,
  getFileUploadSignedURLThunk,
} from "@modules/stepper/redux/thunk/documentThunk";
import { uploadToS3Thunk } from "@store/thunk/UploadS3Thunk";
import { toast } from "react-toastify";
import { AppDispatch } from "@store/store";
import { useDispatch } from "react-redux";
import { submitFinalNOCRequestApi } from "@modules/application-section/application-details/redux/services/thunk/DetailsThunk";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import VisibilityIcon from "@mui/icons-material/Visibility";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import { fetchApplicationDetails } from "@modules/provisional-payment/redux/thunk/application-detailThunk";
import ConfirmationFinalModal from "@modules/application-section-row/commonModal/ConfirmationFinalModal";
import SuccessFinalNocModal from "@modules/application-section-row/commonModal/SuccessFinalNocModal";
import { getSignedPreviewUrl } from "../../utils/getViewSignedUrlHandler";
import { genericUploadThunk } from "@modules/provisional-payment/redux/thunk/uploadThunk";
import { productionCheck } from "@components/buttonGroupView/utils";

// Updated Types for multiple files
interface DocumentData {
  fileName: string;
  objectKey: string;
  documentKey: string;
  category: string;
  fileType: "pdf" | "image" | "video";
  previewUrl?: string;
  uploadProgress?: number;
  isUploading?: boolean;
}

interface UploadBoxProps {
  label: string;
  uploadLabel?: string;
  isPhoto?: boolean;
  isVideo?: boolean;
  required?: boolean;
  note?: string;
  category: string;
  onFileUpload: (file: File, category: string) => Promise<boolean>;
  uploadedDocuments: DocumentData[];
  onRemoveDocument: (objectKey: string) => void;
  multiple?: boolean;
  maxFiles?: number;
  onGenerateSignedUrl: (objectKey: string) => Promise<void>;
}

// Preview Modal Component
const FilePreviewModal = ({
  open,
  onClose,
  file,
  fileType,
}: {
  open: boolean;
  onClose: () => void;
  file: DocumentData;
  fileType: "image" | "video" | "pdf";
}) => {
  const getPreviewContent = () => {
    switch (fileType) {
      case "image":
        return (
          <CardMedia
            component="img"
            image={file.previewUrl || URL.createObjectURL(new Blob())}
            alt={file.fileName}
            sx={{ maxHeight: "700px", objectFit: "contain", width: "900px" }}
          />
        );
      case "video":
        return (
          <CardMedia
            component="video"
            controls
            sx={{ maxHeight: "700px", objectFit: "contain", width: "900px" }}
          >
            <source src={file.previewUrl} type="video/mp4" />
            Your browser does not support the video tag.
          </CardMedia>
        );
      case "pdf":
        return (
          <Box>
            <iframe
              src={file.previewUrl}
              width="1000px"
              height="600px"
              title={file.fileName}
              style={{ border: "none" }}
            />
          </Box>
        );
      default:
        return null;
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        width: "100%",
      }}
    >
      <Card sx={{ maxWidth: "100%", maxHeight: "90vh", overflow: "auto" }}>
        {getPreviewContent()}
        <CardContent>
          <Typography
            variant="h6"
            component="h2"
            display={"flex"}
            justifyContent={"space-between"}
            alignItems={"center"}
          >
            {file.fileName}{" "}
            <Button
              variant="outlined"
              sx={{
                borderRadius: "8px",
                fontFamily: "Inter",
                border: "1px solid #D0D5DD",
                fontSize: "14px",
                color: "#3A1C73",
                fontWeight: 500,
                px: 2,
                py: 0.5,
                background: "#fff",
                boxShadow: "none",
                textTransform: "none",
                "&:hover": { background: "#F5F5F5" },
              }}
              onClick={onClose}
            >
              close
            </Button>
          </Typography>
        </CardContent>
      </Card>
    </Modal>
  );
};

const UploadBox = ({
  label,
  uploadLabel = "Upload File",
  isPhoto = false,
  isVideo = false,
  required = false,
  note,
  category,
  onFileUpload,
  uploadedDocuments,
  onRemoveDocument,
  multiple = true,
  maxFiles = 10,
  onGenerateSignedUrl,
}: UploadBoxProps) => {
  const [isDragging, setIsDragging] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [selectedFile, setSelectedFile] = useState<DocumentData | null>(null);
  const fileInputRef = React.useRef<HTMLInputElement>(null);

  const handleClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = async (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const files = Array.from(event.target.files || []);
    if (files.length === 0) return;

    const currentCategoryFiles = uploadedDocuments.filter(
      (doc) => doc.category === category,
    );
    if (currentCategoryFiles.length + files.length > maxFiles) {
      toast.error(`Maximum ${maxFiles} files allowed for ${label}`);
      event.target.value = "";
      return;
    }

    let allSuccess = true;

    for (const file of files) {
      const result = await onFileUpload(file, category);
      if (result === false) allSuccess = false;
    }

    if (allSuccess) {
      const message =
        files.length > 1
          ? `${files.length} files uploaded successfully`
          : `${files[0].name} uploaded successfully`;
      toast.success(message);
    }

    event.target.value = "";
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = async (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);

    const files = Array.from(e.dataTransfer.files);
    if (files.length === 0) return;

    const currentCategoryFiles = uploadedDocuments.filter(
      (doc) => doc.category === category,
    );
    if (currentCategoryFiles.length + files.length > maxFiles) {
      toast.error(`Maximum ${maxFiles} files allowed for ${label}`);
      return;
    }

    let allSuccess = true;

    for (const file of files) {
      const result = await onFileUpload(file, category);
      if (result === false) allSuccess = false;
    }

    if (allSuccess && files.length > 1) {
      toast.success(`${files.length} files uploaded successfully`);
    }
  };

  const getAcceptType = () => {
    if (isPhoto) return "image/*";
    if (isVideo) return "video/*";
    return ".pdf";
  };

  const getObjectKey = (path?: string) => {
    if (!path) return undefined;
    return path.replace(/^noc-document\/(row|access)\//, "");
  };

  const handlePreview = async (file: DocumentData) => {
    setSelectedFile(file);
    setPreviewOpen(true);
    const objectKey = getObjectKey(file?.objectKey);

    if (!objectKey) return;

    await onGenerateSignedUrl(objectKey);
  };

  const handleClosePreview = () => {
    setPreviewOpen(false);
    setSelectedFile(null);
  };

  const categoryDocuments = uploadedDocuments.filter(
    (doc) => doc.category === category,
  );

  const renderFilePreview = (file: DocumentData) => {
    switch (file.fileType) {
      case "image":
        return (
          <Card sx={{ maxWidth: "100%", m: 0.5, position: "relative" }}>
            {file.isUploading && (
              <LinearProgress
                variant="determinate"
                value={file.uploadProgress || 0}
                sx={{ position: "absolute", top: 0, left: 0, right: 0 }}
              />
            )}
            <Box display={"flex"}>
              <CardMedia
                component="img"
                width={"100px"}
                height="80px"
                image={file.previewUrl}
                alt={file.fileName}
                sx={{
                  objectFit: "cover",
                  cursor: file.isUploading ? "not-allowed" : "pointer",
                  opacity: file.isUploading ? 0.7 : 1,
                }}
                onClick={() => !file.isUploading && handlePreview(file)}
              />
            </Box>

            <CardContent sx={{ p: 1, "&:last-child": { pb: 1 } }}>
              <Typography variant="caption" noWrap>
                {file.fileName}
                {file.isUploading && ` (${file.uploadProgress}%)`}
              </Typography>
            </CardContent>
          </Card>
        );

      case "video":
        return (
          <Card
            sx={{
              maxWidth: "fit-content",
              height: "fit-content",
              m: 0.5,
              position: "relative",
            }}
          >
            {file.isUploading && (
              <LinearProgress
                variant="determinate"
                value={file.uploadProgress || 0}
                sx={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  right: 0,
                  zIndex: 1,
                }}
              />
            )}
            <CardMedia
              component="div"
              sx={{
                height: "100px",
                width: "170px",
                bgcolor: "#000",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                cursor: file.isUploading ? "not-allowed" : "pointer",
                opacity: file.isUploading ? 0.7 : 1,
              }}
              onClick={() => !file.isUploading && handlePreview(file)}
            >
              <PlayArrowIcon sx={{ color: "white", fontSize: 40 }} />
            </CardMedia>
            <CardContent sx={{ p: 1, "&:last-child": { pb: 1 } }}>
              <Typography variant="caption" noWrap>
                {file.fileName}
                {file.isUploading && ` (${file.uploadProgress}%)`}
              </Typography>
            </CardContent>
          </Card>
        );

      case "pdf":
        return (
          <Card sx={{ maxWidth: "100%", m: 0.5, position: "relative", pb: 0 }}>
            {file.isUploading && (
              <LinearProgress
                variant="determinate"
                value={file.uploadProgress || 0}
                sx={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  right: 0,
                  zIndex: 1,
                }}
              />
            )}
            <CardContent
              sx={{
                p: 1,
                display: "flex",
                flexDirection: "column",
                cursor: file.isUploading ? "not-allowed" : "pointer",
                opacity: file.isUploading ? 0.7 : 1,
                paddingBottom: "3px !important",
              }}
              onClick={() => !file.isUploading && handlePreview(file)}
            >
              <PictureAsPdfIcon sx={{ fontSize: 25, color: "#E53935" }} />
              <Typography variant="caption" display="block" noWrap>
                {file.fileName}
                {file.isUploading && ` (${file.uploadProgress}%)`}
              </Typography>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  mt: 0.5,
                }}
              >
                <VisibilityIcon sx={{ fontSize: 14, color: "#666", mr: 0.5 }} />
                <Typography variant="caption" color="#666">
                  View PDF
                </Typography>
              </Box>
            </CardContent>
          </Card>
        );

      default:
        return (
          <Chip
            label={file.fileName}
            onDelete={() =>
              !file.isUploading && onRemoveDocument(file.objectKey)
            }
            deleteIcon={<ClearIcon />}
            variant="outlined"
            size="small"
            sx={{
              mr: 1,
              mb: 1,
              opacity: file.isUploading ? 0.7 : 1,
            }}
            disabled={file.isUploading}
          />
        );
    }
  };

  return (
    <Box mb={3}>
      <Typography
        mb={1}
        sx={{
          fontFamily: "Inter, sans-serif",
          fontWeight: 500,
          fontStyle: "bold",
          fontSize: "14px",
          lineHeight: "24px",
          letterSpacing: "-0.5px",
        }}
      >
        {label} {required && <span style={{ color: "red" }}>*</span>}
      </Typography>
      {note && (
        <Typography
          color="error"
          sx={{ fontSize: 13, mb: 1, color: "#996601" }}
        >
          {note}
        </Typography>
      )}

      <Box
        sx={{
          border: `1.5px dashed ${isDragging ? "#3A1C73" : "#C7C7C7"}`,
          borderRadius: 2,
          p: 2,
          textAlign: "center",
          bgcolor: isDragging ? "#F0F0F0" : "#FAFAFA",
          cursor: "pointer",
          transition: "all 0.2s ease",
          "&:hover": {
            borderColor: "#3A1C73",
            bgcolor: "#F5F5F5",
          },
        }}
        onClick={handleClick}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      >
        <CloudUploadOutlinedIcon
          sx={{
            color: "#3A1C73",
            mb: 1,
            width: 30,
            height: 22,
          }}
        />
        <Typography
          sx={{
            fontFamily: "Inter, sans-serif",
            fontWeight: 500,
            fontStyle: "normal",
            fontSize: "14px",
            lineHeight: "20px",
            letterSpacing: "-0.5px",
            textAlign: "center",
          }}
        >
          Drag & Drop File{multiple ? "s" : ""} or{" "}
          <span
            style={{
              color: "#3A1C73",
              cursor: "pointer",
              textDecoration: "underline",
            }}
          >
            {uploadLabel}
          </span>
          <br />
          <span style={{ fontSize: 13, color: "#757575" }}>
            Please upload up to 20 MB per file.{" "}
            {multiple && `Max ${maxFiles} files.`}
          </span>
        </Typography>

        <input
          type="file"
          ref={fileInputRef}
          style={{ display: "none" }}
          accept={getAcceptType()}
          onChange={handleFileChange}
          multiple={multiple}
        />
      </Box>

      {/* Uploaded Files Preview */}
      {categoryDocuments.length > 0 && (
        <Box mt={2}>
          <Typography variant="body2" color="textSecondary" mb={1}>
            Uploaded files ({categoryDocuments.length}):
          </Typography>
          <Box
            sx={{ display: "flex", gap: 1, overflow: "auto" }}
            width={"100%"}
          >
            {categoryDocuments.map((doc) => (
              <Box key={doc.objectKey} sx={{ position: "relative" }}>
                {renderFilePreview(doc)}
                {!doc.isUploading && (
                  <IconButton
                    size="small"
                    onClick={() => onRemoveDocument(doc.objectKey)}
                    sx={{
                      position: "absolute",
                      zIndex: 5,
                      top: 4,
                      right: 4,
                      bgcolor: "#00000042",
                      color: "white",
                      width: 25,
                      borderRadius: "unset",
                      height: 22,
                      "&:hover": { bgcolor: "error.dark" },
                    }}
                  >
                    <ClearIcon sx={{ fontSize: 14 }} />
                  </IconButton>
                )}
              </Box>
            ))}
          </Box>
        </Box>
      )}

      {/* Preview Modal */}
      {selectedFile && (
        <FilePreviewModal
          open={previewOpen}
          onClose={handleClosePreview}
          file={selectedFile}
          fileType={selectedFile.fileType}
        />
      )}
    </Box>
  );
};

const FinalNOCModal: React.FC<{
  openSlider: boolean;
  setOpenSlider: (open: boolean) => void;
  applicationId: number;
  onSuccess: () => void;
}> = ({ openSlider, setOpenSlider, applicationId, onSuccess }) => {
  const dispatch = useDispatch<AppDispatch>();
  const [completed, setCompleted] = useState<string>("");
  const [variation, setVariation] = useState<string>("yes");
  const [uploadedDocuments, setUploadedDocuments] = useState<DocumentData[]>(
    [],
  );

  const [isConfirmationFLOpen, setIsConfirmationFLOpen] = useState(false);
  const [previewDocUrl, setPreviewDocUrl] = useState<string>();

  // ✅ Centralized reset function
  const resetAllState = () => {
    setCompleted("");
    setVariation("yes");
    setUploadedDocuments([]);
    setIsConfirmationFLOpen(false);
    setPreviewDocUrl(undefined);
  };

  // ✅ Handler for closing the drawer — resets state first
  const handleCloseDrawer = () => {
    resetAllState();
    setOpenSlider(false);
  };

  const generateSignedUrl = async (doc_name: string) => {
    try {
      const signedUrl = await getSignedPreviewUrl({
        document_name: doc_name,
        dispatch,
        getSignedUrlThunk: getFileSignedURLThunk,
      });

      if (!signedUrl) return;

      setPreviewDocUrl(signedUrl);
    } catch (e) {
      console.error(e);
    }
  };

  const handleFileUpload = async (
    file: File,
    category: string,
  ): Promise<boolean> => {
    if (file.size > 21 * 1024 * 1024) {
      toast.error("File size must be less than 20 MB");
      return false;
    }

    const tempObjectKey = `temp-${category}-${Date.now()}-${file.name}`;
    const previewUrl = URL.createObjectURL(file);

    const tempDocument: DocumentData = {
      fileName: file.name,
      objectKey: tempObjectKey,
      documentKey: category,
      category,
      fileType: file.type.startsWith("image/")
        ? "image"
        : file.type.startsWith("video/")
          ? "video"
          : "pdf",
      previewUrl,
      isUploading: true,
      uploadProgress: 0,
    };

    setUploadedDocuments((prev) => [...prev, tempDocument]);

    try {
      const signedURLResponse = await dispatch(
        genericUploadThunk({
          file,
          moduleType: "access",
        }),
      );
      if (!signedURLResponse?.payload?.success) {
        const details =
          signedURLResponse?.payload?.details ||
          signedURLResponse?.payload?.response?.data?.details;
        toast.error(
          Array.isArray(details)
            ? details[0]
            : typeof details === "string"
              ? details
              : "Something Went Wrong!",
        );
        setUploadedDocuments((prev) =>
          prev.filter((doc) => doc.objectKey !== tempObjectKey),
        );
        return false;
      }

      const { data } = signedURLResponse.payload;

      setUploadedDocuments((prev) =>
        prev.map((doc) =>
          doc.objectKey === tempObjectKey
            ? { ...doc, uploadProgress: 50 }
            : doc,
        ),
      );
      if (!productionCheck) {
        const uploadSuccess = await dispatch(
          uploadToS3Thunk({
            signedUrl: data.signedUrl,
            file,
            contentType: file.type,
          }),
        ).unwrap();

        if (!uploadSuccess) throw new Error("S3 upload failed");
      }

      const finalDocument: DocumentData = {
        fileName: data.originalFileName,
        objectKey: data.objectKey,
        documentKey: category,
        category,
        fileType: file.type.startsWith("image/")
          ? "image"
          : file.type.startsWith("video/")
            ? "video"
            : "pdf",
        previewUrl,
      };

      setUploadedDocuments((prev) =>
        prev.map((doc) =>
          doc.objectKey === tempObjectKey ? finalDocument : doc,
        ),
      );

      return true;
    } catch (error) {
      console.error("Upload failed:", error);
      setUploadedDocuments((prev) =>
        prev.filter((doc) => doc.objectKey !== tempObjectKey),
      );
      toast.error(`Failed to upload ${file.name}`);
      return false;
    }
  };

  const handleRemoveDocument = (objectKey: string) => {
    setUploadedDocuments((prev) =>
      prev.filter((doc) => doc.objectKey !== objectKey),
    );
  };

  const getFinalPayload = () => {
    return {
      applicationId: applicationId,
      is_construction_completed: completed === "yes",
      final_License_request: true,
      provisional_drawing_variation: variation === "yes",
      documents: uploadedDocuments
        .filter((doc) => !doc.isUploading)
        .map((doc) => ({
          fileName: doc.fileName,
          objectKey: doc.objectKey,
          documentKey: doc.category,
        })),
    };
  };

  const getErrorMessage = (error: any): string => {
    if (typeof error === "string") {
      return error;
    }

    return (
      error?.message ||
      error?.data?.message ||
      error?.payload?.message ||
      error?.response?.data?.message ||
      error?.error?.message ||
      "Submission failed. Please try again."
    );
  };

  const handleSubmit = async () => {
    const uploadingFiles = uploadedDocuments.filter((doc) => doc.isUploading);

    if (uploadingFiles.length > 0) {
      toast.error("Please wait for all files to finish uploading");
      return;
    }

    if (completed === "yes") {
      const requiredCategories = [
        "proposed",
        "adjoining",
        "opp_proposed",
        "completion_report",
      ];

      const missingCategories = requiredCategories.filter(
        (cat) =>
          !uploadedDocuments.some(
            (doc) => doc.category === cat && !doc.isUploading,
          ),
      );

      if (missingCategories.length > 0) {
        toast.error("Please upload all required documents");
        return;
      }

      const photoCategories = ["proposed", "adjoining", "opp_proposed"];

      const insufficientPhotos = photoCategories.filter((cat) => {
        const photoCount = uploadedDocuments.filter(
          (doc) =>
            doc.category === cat &&
            doc.fileType === "image" &&
            !doc.isUploading,
        ).length;

        return photoCount < 4;
      });

      if (insufficientPhotos.length > 0) {
        toast.error("Please upload at least 4 photos for each photo category");
        return;
      }
    }

    const payload = getFinalPayload();

    try {
      const result = await dispatch(submitFinalNOCRequestApi(payload)).unwrap();

      if (!result?.success) {
        toast.error(result?.message || "Submission failed. Please try again.");
        return;
      }

      onSuccess?.();
      setIsConfirmationFLOpen(true);
    } catch (error: any) {
      console.error("Final NOC submission error:", error);

      const errorMessage = getErrorMessage(error);

      toast.error(errorMessage);

      setIsConfirmationFLOpen(false);
    }
  };

  const isSubmitDisabled = () => {
    if (!completed) return true;
    if (completed === "no") return true;

    const uploadingFiles = uploadedDocuments.filter((doc) => doc.isUploading);
    if (uploadingFiles.length > 0) return true;

    if (completed === "yes") {
      const requiredCategories = [
        "proposed",
        "adjoining",
        "opp_proposed",
        "completion_report",
      ];

      const missingCategories = requiredCategories.filter(
        (cat) =>
          !uploadedDocuments.some(
            (doc) => doc.category === cat && !doc.isUploading,
          ),
      );

      if (missingCategories.length > 0) return true;

      const photoCategories = ["proposed", "adjoining", "opp_proposed"];
      const insufficientPhotos = photoCategories.some((cat) => {
        const photoCount = uploadedDocuments.filter(
          (doc) =>
            doc.category === cat &&
            doc.fileType === "image" &&
            !doc.isUploading,
        ).length;
        return photoCount < 4;
      });

      if (insufficientPhotos) return true;
    }

    return false;
  };

  return (
    <>
      <Drawer
        anchor="right"
        open={openSlider}
        onClose={handleCloseDrawer}
        sx={{
          "& .MuiDrawer-paper": {
            width: { xs: "100vw", sm: 450 },
            boxSizing: "border-box",
          },
        }}
      >
        {/* Header */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            px: 3,
            py: 2,
            borderBottom: "1px solid #eee",
          }}
        >
          <Typography fontWeight={700} fontSize={18}>
            Request to Final NOC
          </Typography>
          <IconButton onClick={handleCloseDrawer}>
            <CloseIcon />
          </IconButton>
        </Box>

        {/* Content */}
        <Box sx={{ px: 3, py: 2, flex: 1, overflowY: "auto" }}>
          {/* Upload Status Alert */}
          {uploadedDocuments.some((doc) => doc.isUploading) && (
            <Alert severity="info" sx={{ mb: 2 }}>
              Uploading files... Please wait.
            </Alert>
          )}

          {/* Upload Summary */}
          {uploadedDocuments.filter((doc) => !doc.isUploading).length > 0 && (
            <Alert severity="success" sx={{ mb: 2 }}>
              {uploadedDocuments.filter((doc) => !doc.isUploading).length}{" "}
              file(s) uploaded successfully
            </Alert>
          )}

          <Typography fontWeight={500} mb={2}>
            Whether construction as per approved drawing/ layout in accordance
            with provisional NOC Completed?
          </Typography>

          <RadioGroup
            row
            value={completed}
            onChange={(e) => setCompleted(e.target.value)}
          >
            <FormControlLabel
              value="yes"
              control={<Radio sx={{ "&.Mui-checked": { color: "#3A1C73" } }} />}
              label="Yes"
            />
            <FormControlLabel
              value="no"
              control={<Radio sx={{ "&.Mui-checked": { color: "#3A1C73" } }} />}
              label="No"
            />
          </RadioGroup>

          {completed === "yes" && (
            <>
              <UploadBox
                label="Upload Video For Fuel Station Area"
                uploadLabel="Upload Video"
                isVideo
                category="fuel_station_video"
                onFileUpload={handleFileUpload}
                uploadedDocuments={uploadedDocuments}
                onRemoveDocument={handleRemoveDocument}
                multiple={true}
                maxFiles={4}
                onGenerateSignedUrl={generateSignedUrl}
              />

              <UploadBox
                label="Upload Video for Proposed access details (acceleration/ deceleration/service road)."
                uploadLabel="Upload Video"
                isVideo
                category="proposed_access_video"
                onFileUpload={handleFileUpload}
                uploadedDocuments={uploadedDocuments}
                onRemoveDocument={handleRemoveDocument}
                multiple={true}
                maxFiles={4}
                onGenerateSignedUrl={generateSignedUrl}
              />

              <UploadBox
                label="Upload photos of proposed plot"
                uploadLabel="Upload Photo"
                isPhoto
                required
                note="Uploading minimum 4 photos is mandatory"
                category="proposed"
                onFileUpload={handleFileUpload}
                uploadedDocuments={uploadedDocuments}
                onRemoveDocument={handleRemoveDocument}
                multiple={true}
                maxFiles={10}
                onGenerateSignedUrl={generateSignedUrl}
              />

              <UploadBox
                label="Upload the photos of the adjoining plots covering the acceleration lane/ Deceleration lane/ service road"
                uploadLabel="Upload Photo"
                isPhoto
                required
                note="Uploading minimum 4 photos is mandatory"
                category="adjoining"
                onFileUpload={handleFileUpload}
                uploadedDocuments={uploadedDocuments}
                onRemoveDocument={handleRemoveDocument}
                multiple={true}
                maxFiles={10}
                onGenerateSignedUrl={generateSignedUrl}
              />

              <UploadBox
                label="Upload the photos of showing details of site opposite to proposed plot"
                uploadLabel="Upload Photo"
                isPhoto
                required
                note="Uploading minimum 4 photos is mandatory"
                category="opp_proposed"
                onFileUpload={handleFileUpload}
                uploadedDocuments={uploadedDocuments}
                onRemoveDocument={handleRemoveDocument}
                multiple={true}
                maxFiles={10}
                onGenerateSignedUrl={generateSignedUrl}
              />

              <UploadBox
                label="Any other photographs essential for detailing of the proposal"
                uploadLabel="Upload Photo"
                isPhoto
                category="others"
                onFileUpload={handleFileUpload}
                uploadedDocuments={uploadedDocuments}
                onRemoveDocument={handleRemoveDocument}
                multiple={true}
                maxFiles={10}
                onGenerateSignedUrl={generateSignedUrl}
              />

              <UploadBox
                label="Completion/Compliance Report"
                uploadLabel="Upload File"
                required
                category="completion_report"
                onFileUpload={handleFileUpload}
                uploadedDocuments={uploadedDocuments}
                onRemoveDocument={handleRemoveDocument}
                multiple={true}
                maxFiles={4}
                onGenerateSignedUrl={generateSignedUrl}
              />

              <UploadBox
                label="As-built Drawing (for any minor modifications)."
                uploadLabel="Upload File"
                category="as_built_drawing"
                onFileUpload={handleFileUpload}
                uploadedDocuments={uploadedDocuments}
                onRemoveDocument={handleRemoveDocument}
                multiple={true}
                maxFiles={4}
                onGenerateSignedUrl={generateSignedUrl}
              />

              <Typography fontWeight={600} mt={2} mb={1} sx={{ fontSize: 15 }}>
                Is there any variation with respect to the approved drawing
                issued during the Provisional NOC?
              </Typography>

              <RadioGroup
                row
                value={variation}
                onChange={(e) => setVariation(e.target.value)}
              >
                <FormControlLabel
                  value="yes"
                  control={
                    <Radio sx={{ "&.Mui-checked": { color: "#3A1C73" } }} />
                  }
                  label="Yes"
                />
                <FormControlLabel
                  value="no"
                  control={
                    <Radio sx={{ "&.Mui-checked": { color: "#3A1C73" } }} />
                  }
                  label="No"
                />
              </RadioGroup>

              {variation === "yes" && (
                <>
                  <UploadBox
                    label="Details / Drawing of variations"
                    uploadLabel="Upload File"
                    category="variation_drawing"
                    onFileUpload={handleFileUpload}
                    uploadedDocuments={uploadedDocuments}
                    onRemoveDocument={handleRemoveDocument}
                    multiple={true}
                    maxFiles={4}
                    onGenerateSignedUrl={generateSignedUrl}
                  />
                </>
              )}
            </>
          )}

          {completed === "no" && (
            <Typography
              sx={{ color: "red", mt: 2, fontWeight: 500 }}
              variant="body2"
            >
              You are required to complete the construction in accordance with
              the approved drawing/layout and the provisional NOC before
              applying for the final license.
            </Typography>
          )}
        </Box>

        {/* Footer */}
        <Divider />
        <Box sx={{ px: 3, py: 2 }}>
          <Button
            fullWidth
            variant="contained"
            color="primary"
            onClick={handleSubmit}
            disabled={isSubmitDisabled()}
            sx={{
              height: 40,
              gap: "8px",
              borderRadius: "24px",
              background: isSubmitDisabled() ? "#9E9E9E" : "#3A1C73",
              color: "#fff",
              fontWeight: 600,
              fontSize: 16,
              textTransform: "none",
              boxShadow: "none",
              "&:hover": {
                background: isSubmitDisabled() ? "#9E9E9E" : "#4B2996",
              },
            }}
          >
            {uploadedDocuments.some((doc) => doc.isUploading)
              ? "Uploading..."
              : "Submit Request"}
          </Button>
        </Box>
      </Drawer>
      <SuccessFinalNocModal
        isConfirmationFLOpen={isConfirmationFLOpen}
        setIsConfirmationFLOpen={(open) => {
          setIsConfirmationFLOpen(open);
          // ✅ Reset state when success modal closes too
          if (!open) {
            resetAllState();
          }
        }}
        setOpenSlider={(open) => {
          setOpenSlider(open);
          // ✅ Reset state when drawer is closed via success modal
          if (!open) {
            resetAllState();
          }
        }}
        onSuccessClose={() => dispatch(fetchApplicationDetails(applicationId))}
      />
    </>
  );
};

export default FinalNOCModal;